import { useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  FileText,
  LayoutGrid,
  Layers3,
  Play,
  Save,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  QUOTE_COLUMNS,
  type QuoteComponent,
  type QuoteColumnId,
  type QuoteLayout,
  type QuoteSnapshot,
  type QuoteWorkspaceSummary,
} from "./model";
import { QuoteComponentsPanel } from "./QuoteComponentsPanel";

export type QuoteSidePanel = "library" | "components" | "customize";

interface QuoteSidebarProps {
  panel: QuoteSidePanel;
  snapshot: QuoteSnapshot;
  workspaces: QuoteWorkspaceSummary[];
  currentId: string | null;
  userId: string | null;
  canWrite: boolean;
  isAdmin: boolean;
  loading: boolean;
  selectedMonths: string[];
  onClose(): void;
  onLoad(workspace: QuoteWorkspaceSummary): void;
  onApplyPreset(workspace: QuoteWorkspaceSummary): void;
  onDuplicate(workspace: QuoteWorkspaceSummary): void;
  onDelete(workspace: QuoteWorkspaceSummary): void;
  onAddComponent(component: QuoteComponent): void;
  onUpdateComponent(component: QuoteComponent): void;
  onDeleteComponent(component: QuoteComponent): void;
  onApplyComponent(component: QuoteComponent): void;
  onCopyComponent(component: QuoteComponent): void;
  onSettingsChange(settings: QuoteSnapshot["settings"]): void;
  onApplyDefaultRisk(overwrite: boolean): void;
  onLayoutChange(layout: QuoteLayout): void;
  onNotesChange(notes: string): void;
  onSetRange(startMonth: string, endMonth: string): void;
  onResetLayout(): void;
}

export function QuoteSidebar(props: QuoteSidebarProps) {
  return (
    <aside className="flex min-h-0 w-[340px] shrink-0 flex-col border-l border-border/70 bg-bg shadow-[-12px_0_30px_rgba(0,0,0,0.04)]">
      <div className="flex h-11 items-center border-b border-border/70 px-3">
        {props.panel === "library" ? <Save className="mr-2 h-4 w-4 text-accent" /> : props.panel === "components" ? <Layers3 className="mr-2 h-4 w-4 text-accent" /> : <SlidersHorizontal className="mr-2 h-4 w-4 text-accent" />}
        <h2 className="font-semibold">{props.panel === "library" ? "Quotes & presets" : props.panel === "components" ? "Named quote strips" : "Customize sheet"}</h2>
        <button type="button" onClick={props.onClose} className="ml-auto rounded p-1 text-muted hover:bg-accent/10 hover:text-fg" aria-label="Close side panel">
          <X className="h-4 w-4" />
        </button>
      </div>
      {props.panel === "library" ? (
        <LibraryPanel {...props} />
      ) : props.panel === "components" ? (
        <QuoteComponentsPanel
          snapshot={props.snapshot}
          selectedMonths={props.selectedMonths}
          canWrite={props.canWrite}
          onAdd={props.onAddComponent}
          onUpdate={props.onUpdateComponent}
          onDelete={props.onDeleteComponent}
          onApply={props.onApplyComponent}
          onCopy={props.onCopyComponent}
        />
      ) : (
        <CustomizePanel {...props} />
      )}
    </aside>
  );
}

function LibraryPanel({
  workspaces,
  currentId,
  userId,
  canWrite,
  isAdmin,
  loading,
  onLoad,
  onApplyPreset,
  onDuplicate,
  onDelete,
}: QuoteSidebarProps) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return workspaces.filter((workspace) =>
      !needle || `${workspace.name} ${workspace.ownerLabel ?? ""}`.toLowerCase().includes(needle)
    );
  }, [search, workspaces]);
  const quotes = filtered.filter((workspace) => workspace.kind === "quote");
  const presets = filtered.filter((workspace) => workspace.kind === "preset");

  return (
    <div className={cn("min-h-0 flex-1 overflow-y-auto p-3", loading && "pointer-events-none opacity-60")} aria-busy={loading}>
      <input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search quotes and presets…"
        className="mb-3 h-8 w-full rounded border border-border bg-bg px-2 text-xs outline-none focus:border-accent"
      />
      {loading && <div className="py-8 text-center text-xs text-muted">Loading saved workspaces…</div>}
      {!loading && workspaces.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted">
          Nothing saved yet. Save this sheet as a quote or a reusable preset.
        </div>
      )}
      <WorkspaceSection title="Saved quotes" icon={<FileText className="h-3.5 w-3.5" />} empty="No matching quotes">
        {quotes.map((workspace) => (
          <WorkspaceCard
            key={workspace.id}
            workspace={workspace}
            active={workspace.id === currentId}
            primaryLabel="Open"
            onPrimary={() => onLoad(workspace)}
            canManage={canWrite && (isAdmin || workspace.ownerUserId === userId)}
            canDuplicate={canWrite}
            onDuplicate={() => onDuplicate(workspace)}
            onDelete={() => onDelete(workspace)}
          />
        ))}
        {quotes.length === 0 && <EmptyRow text="No matching quotes" />}
      </WorkspaceSection>
      <WorkspaceSection title="Presets" icon={<LayoutGrid className="h-3.5 w-3.5" />} empty="No matching presets">
        {presets.map((workspace) => (
          <WorkspaceCard
            key={workspace.id}
            workspace={workspace}
            active={workspace.id === currentId}
            primaryLabel="Apply"
            primaryIcon={<Play className="h-3 w-3" />}
            onPrimary={() => onApplyPreset(workspace)}
            secondaryLabel="Edit"
            onSecondary={() => onLoad(workspace)}
            canManage={canWrite && (isAdmin || workspace.ownerUserId === userId)}
            canDuplicate={canWrite}
            onDuplicate={() => onDuplicate(workspace)}
            onDelete={() => onDelete(workspace)}
          />
        ))}
        {presets.length === 0 && <EmptyRow text="No matching presets" />}
      </WorkspaceSection>
    </div>
  );
}

function WorkspaceSection({ title, icon, children }: { title: string; icon: React.ReactNode; empty: string; children: React.ReactNode }) {
  return (
    <section className="mb-5">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">
        {icon}{title}
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function WorkspaceCard({
  workspace,
  active,
  primaryLabel,
  primaryIcon,
  secondaryLabel,
  onPrimary,
  onSecondary,
  canManage,
  canDuplicate,
  onDuplicate,
  onDelete,
}: {
  workspace: QuoteWorkspaceSummary;
  active: boolean;
  primaryLabel: string;
  primaryIcon?: React.ReactNode;
  secondaryLabel?: string;
  onPrimary(): void;
  onSecondary?(): void;
  canManage: boolean;
  canDuplicate: boolean;
  onDuplicate(): void;
  onDelete(): void;
}) {
  return (
    <div className={cn("rounded-md border p-2.5", active ? "border-accent bg-accent/5" : "border-border/70")}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium" title={workspace.name}>{workspace.name}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[10px] text-muted">
            <span>{workspace.visibility === "company" ? "Company" : "Private"}</span>
            <span>·</span>
            <span>{workspace.ownerLabel || "Unknown owner"}</span>
            <span>·</span>
            <span>v{workspace.version}</span>
          </div>
          <div className="mt-0.5 text-[10px] text-muted">Updated {formatUpdated(workspace.updatedAt)}</div>
        </div>
        {active && <span className="rounded bg-accent px-1.5 py-0.5 text-[9px] font-semibold text-white">OPEN</span>}
      </div>
      <div className="mt-2 flex items-center gap-1">
        <button type="button" onClick={onPrimary} className="inline-flex h-6 items-center gap-1 rounded border border-accent/60 px-2 text-[10px] text-accent hover:bg-accent/10">
          {primaryIcon}{primaryLabel}
        </button>
        {secondaryLabel && onSecondary && (
          <button type="button" onClick={onSecondary} className="h-6 rounded border border-border px-2 text-[10px] text-muted hover:border-accent/60 hover:text-fg">{secondaryLabel}</button>
        )}
        {canDuplicate && (
          <button type="button" onClick={onDuplicate} className="ml-auto rounded p-1 text-muted hover:bg-accent/10 hover:text-fg" title="Duplicate into my workspace">
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
        {canManage && (
          <>
            <button type="button" onClick={onDelete} className="rounded p-1 text-muted hover:bg-neg-red/10 hover:text-neg-red" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <div className="rounded border border-dashed border-border/60 px-3 py-4 text-center text-[11px] text-muted">{text}</div>;
}

function CustomizePanel({
  snapshot,
  canWrite,
  onSettingsChange,
  onApplyDefaultRisk,
  onLayoutChange,
  onNotesChange,
  onSetRange,
  onResetLayout,
}: QuoteSidebarProps) {
  const firstMonth = snapshot.rows[0]?.month ?? "";
  const lastMonth = snapshot.rows.at(-1)?.month ?? "";
  const [startMonth, setStartMonth] = useState(firstMonth);
  const [endMonth, setEndMonth] = useState(lastMonth);
  useEffect(() => {
    setStartMonth(firstMonth);
    setEndMonth(lastMonth);
  }, [firstMonth, lastMonth]);

  const setSetting = (key: keyof QuoteSnapshot["settings"], value: number) =>
    onSettingsChange({ ...snapshot.settings, [key]: value });
  const hidden = new Set(snapshot.layout.hiddenColumns);
  const rangeMonths = inclusiveMonthCount(startMonth, endMonth);
  const rangeError = rangeMonths > 240
    ? `${rangeMonths} months selected; the maximum is 240.`
    : null;

  return (
    <fieldset disabled={!canWrite} className="min-h-0 flex-1 overflow-y-auto p-3 text-xs disabled:opacity-70">
      {!canWrite && (
        <div className="mb-3 rounded border border-border/70 bg-accent/5 p-2 text-[10px] text-muted">
          This sheet is read-only for your EEX role.
        </div>
      )}
      <SectionTitle title="Quote adders" subtitle="Applied live to every derived physical quote" />
      <div className="grid grid-cols-2 gap-2">
        <NumberSetting label="RON buy adder" suffix="RON" value={snapshot.settings.ronBuyAdder} onChange={(value) => setSetting("ronBuyAdder", value)} />
        <NumberSetting label="RON sell width" suffix="RON" value={snapshot.settings.ronSellAdder} onChange={(value) => setSetting("ronSellAdder", value)} />
        <NumberSetting label="EUR buy adder" suffix="EUR" value={snapshot.settings.eurBuyAdder} onChange={(value) => setSetting("eurBuyAdder", value)} />
        <NumberSetting label="EUR sell width" suffix="EUR" value={snapshot.settings.eurSellAdder} onChange={(value) => setSetting("eurSellAdder", value)} />
      </div>

      <SectionTitle title="New delivery months" subtitle="Default used only when the horizon adds a new row" />
      <NumberSetting label="Risk for new months" suffix="EUR" value={snapshot.settings.defaultRisk} onChange={(value) => setSetting("defaultRisk", value)} />
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button type="button" onClick={() => onApplyDefaultRisk(false)} className="h-7 rounded border border-border text-[10px] text-muted hover:border-accent/60 hover:text-fg">Apply to blanks</button>
        <button type="button" onClick={() => onApplyDefaultRisk(true)} className="h-7 rounded border border-border text-[10px] text-muted hover:border-accent/60 hover:text-fg">Apply to all months</button>
      </div>

      <SectionTitle title="Precision" subtitle="Display only; calculations retain full precision" />
      <div className="grid grid-cols-2 gap-2">
        <NumberSetting label="Price decimals" value={snapshot.settings.priceDecimals} min={0} max={6} step={1} onChange={(value) => setSetting("priceDecimals", Math.round(value))} />
        <NumberSetting label="FX decimals" value={snapshot.settings.fxDecimals} min={0} max={8} step={1} onChange={(value) => setSetting("fxDecimals", Math.round(value))} />
      </div>

      <SectionTitle title="Month horizon" subtitle="Existing month inputs are preserved when the range changes" />
      <div className="grid grid-cols-2 gap-2">
        <MonthPicker label="Start" value={startMonth} onChange={setStartMonth} />
        <MonthPicker label="End" value={endMonth} onChange={setEndMonth} align="right" />
      </div>
      <button
        type="button"
        disabled={!startMonth || !endMonth || startMonth > endMonth || rangeMonths < 1 || rangeMonths > 240}
        onClick={() => onSetRange(startMonth, endMonth)}
        className="mt-2 h-7 w-full rounded border border-border text-[11px] text-muted hover:border-accent/60 hover:text-fg disabled:opacity-40"
      >
        Apply month range
      </button>
      {rangeError && <div className="mt-1 text-[10px] text-neg-red">{rangeError}</div>}

      <SectionTitle title="Sheet layout" subtitle="Drag headers in the grid or reorder here" />
      <div className="mb-2 flex rounded border border-border p-0.5">
        {(["compact", "comfortable"] as const).map((density) => (
          <button
            key={density}
            type="button"
            onClick={() => onLayoutChange({ ...snapshot.layout, density })}
            className={cn("h-7 flex-1 rounded text-[10px] capitalize", snapshot.layout.density === density ? "bg-accent text-white" : "text-muted hover:text-fg")}
          >
            {density}
          </button>
        ))}
      </div>
      <label className="mb-2 flex h-8 items-center gap-2 rounded border border-border/60 px-2 text-[11px]">
        <input
          type="checkbox"
          checked={snapshot.layout.showShapePnl}
          onChange={(event) => onLayoutChange({ ...snapshot.layout, showShapePnl: event.target.checked })}
          className="accent-[rgb(var(--accent))]"
        />
        Show shape P&amp;L analysis
      </label>
      <div className="space-y-1">
        {snapshot.layout.columnOrder.map((id, index) => {
          const column = QUOTE_COLUMNS.find((item) => item.id === id);
          if (!column) return null;
          const canHide = id !== "month";
          return (
            <div key={id} className="flex h-8 items-center rounded border border-border/60 px-2">
              <input
                type="checkbox"
                checked={!hidden.has(id)}
                disabled={!canHide}
                onChange={(event) => {
                  const next = new Set(snapshot.layout.hiddenColumns);
                  if (event.target.checked) next.delete(id);
                  else next.add(id);
                  onLayoutChange({ ...snapshot.layout, hiddenColumns: [...next] });
                }}
                className="mr-2 accent-[rgb(var(--accent))]"
              />
              <span className="min-w-0 flex-1 truncate text-[11px]" title={column.description}>{column.shortTitle}</span>
              <button type="button" disabled={index === 0 || id === "month"} onClick={() => moveColumn(snapshot.layout, id, -1, onLayoutChange)} className="rounded p-1 text-muted hover:text-fg disabled:opacity-20"><ChevronUp className="h-3 w-3" /></button>
              <button type="button" disabled={index === snapshot.layout.columnOrder.length - 1 || id === "month"} onClick={() => moveColumn(snapshot.layout, id, 1, onLayoutChange)} className="rounded p-1 text-muted hover:text-fg disabled:opacity-20"><ChevronDown className="h-3 w-3" /></button>
            </div>
          );
        })}
      </div>
      <button type="button" onClick={onResetLayout} className="mt-2 h-7 w-full rounded border border-border text-[11px] text-muted hover:border-accent/60 hover:text-fg">Reset columns and widths</button>

      <SectionTitle title="Quote notes" subtitle="Saved with this quote snapshot" />
      <textarea
        value={snapshot.metadata.notes}
        onChange={(event) => onNotesChange(event.target.value.slice(0, 2000))}
        rows={5}
        placeholder="Source, assumptions, validity conditions, negotiation notes…"
        className="w-full resize-y rounded border border-border bg-bg p-2 text-xs outline-none focus:border-accent"
      />
    </fieldset>
  );
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function MonthPicker({
  label,
  value,
  onChange,
  align = "left",
}: {
  label: string;
  value: string;
  onChange(value: string): void;
  align?: "left" | "right";
}) {
  const selected = parseMonth(value);
  const [open, setOpen] = useState(false);
  const [visibleYear, setVisibleYear] = useState(selected?.year ?? new Date().getFullYear());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const toggle = () => {
    if (!open) setVisibleYear(selected?.year ?? new Date().getFullYear());
    setOpen((current) => !current);
  };

  return (
    <div ref={containerRef} className="relative space-y-1 text-[10px] text-muted">
      <span>{label}</span>
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex h-8 w-full items-center gap-2 rounded border border-border bg-bg px-2 text-left text-xs text-fg outline-none hover:border-accent/60 focus:border-accent"
      >
        <CalendarDays className="h-3.5 w-3.5 shrink-0 text-muted" />
        <span className="flex-1">{selected ? `${MONTH_NAMES[selected.month - 1]} ${selected.year}` : "Select month"}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-muted transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={`${label} month`}
          className={cn(
            "absolute top-full z-30 mt-1 w-[232px] rounded-md border border-border bg-bg p-2 shadow-lg",
            align === "right" ? "right-0" : "left-0"
          )}
        >
          <div className="mb-2 flex items-center justify-between">
            <button type="button" onClick={() => setVisibleYear((year) => year - 1)} className="rounded p-1 text-muted hover:bg-accent/10 hover:text-fg" aria-label="Previous year">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs font-semibold text-fg">{visibleYear}</span>
            <button type="button" onClick={() => setVisibleYear((year) => year + 1)} className="rounded p-1 text-muted hover:bg-accent/10 hover:text-fg" aria-label="Next year">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {MONTH_NAMES.map((monthName, index) => {
              const month = index + 1;
              const isSelected = selected?.year === visibleYear && selected.month === month;
              return (
                <button
                  key={monthName}
                  type="button"
                  onClick={() => {
                    onChange(`${visibleYear}-${String(month).padStart(2, "0")}`);
                    setOpen(false);
                  }}
                  aria-pressed={isSelected}
                  className={cn(
                    "h-7 rounded text-[11px] text-fg hover:bg-accent/10",
                    isSelected && "bg-accent text-white hover:bg-accent"
                  )}
                >
                  {monthName}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function parseMonth(value: string): { year: number; month: number } | null {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) };
}

function SectionTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-2 mt-5 first:mt-0">
      <div className="text-[11px] font-semibold">{title}</div>
      <div className="text-[10px] text-muted">{subtitle}</div>
    </div>
  );
}

function NumberSetting({
  label,
  suffix,
  value,
  min = -1_000_000_000,
  max = 1_000_000_000,
  step = 0.01,
  onChange,
}: {
  label: string;
  suffix?: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange(value: number): void;
}) {
  return (
    <label className="space-y-1 text-[10px] text-muted">
      <span>{label}</span>
      <div className="flex h-8 items-center rounded border border-border focus-within:border-accent">
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (Number.isFinite(next)) onChange(next);
          }}
          className="min-w-0 flex-1 bg-transparent px-2 text-right font-mono text-xs text-fg outline-none"
        />
        {suffix && <span className="pr-2 text-[9px]">{suffix}</span>}
      </div>
    </label>
  );
}

function moveColumn(layout: QuoteLayout, id: QuoteColumnId, delta: -1 | 1, onChange: (layout: QuoteLayout) => void) {
  const from = layout.columnOrder.indexOf(id);
  const to = from + delta;
  if (from <= 0 || to <= 0 || to >= layout.columnOrder.length) return;
  const order = [...layout.columnOrder];
  const [moved] = order.splice(from, 1);
  if (moved) order.splice(to, 0, moved);
  onChange({ ...layout, columnOrder: order });
}

function formatUpdated(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function inclusiveMonthCount(startMonth: string, endMonth: string): number {
  const start = /^(\d{4})-(\d{2})$/.exec(startMonth);
  const end = /^(\d{4})-(\d{2})$/.exec(endMonth);
  if (!start || !end) return 0;
  const startIndex = Number(start[1]) * 12 + Number(start[2]) - 1;
  const endIndex = Number(end[1]) * 12 + Number(end[2]) - 1;
  return endIndex >= startIndex ? endIndex - startIndex + 1 : 0;
}
